import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@material-tailwind/react";
import { api } from "../../../../services/api/api";
import {
  SearchOptions,
  SearchQuery,
  searchWithFilters,
  toSearchOrder,
} from "../../../../services/api/search";
import { useCustomersFilters } from "../../../../hooks/filters";
import { Pagination } from "../../../../components/pagination/Pagination";
import FilterDrawer from "../../../../components/filter-drawer/FilterDrawer";
import FiltersDisplay from "../../../../components/filter/FiltersDisplay";
import { GenericListLayout } from "../../../../components/generic-list-view/GenericListLayout";
import CustomerFilterForm, { getQueriesFromFilter } from "../../../customers/filter/CustomerFilterForm";
import { useDrawerLayoutDispatch } from "../../../../components/drawer-layout/DrawerLayout";
import AdminCustomersTable from "./AdminCustomersTable";
import MergeCustomersDrawer from "./MergeCustomersDrawer";
import { emptyRecordedData } from "./AdminCustomerDisplay";
import "./AdminCustomers.scss";
import { useTablePageSize } from "../../../../hooks/useTablePageSize";

export default function AdminCustomers() {
  const dispatch = useDrawerLayoutDispatch();
  const [customersPageState, setCustomersPageState] = useState<EasyTech.Customer[]>([]);
  const [pageNumberState, setPageNumberState] = useState<number>(1);
  const [pageState, setPageState] = useState<number>(0);
  const [orderState, setOrderState] = useState<{ head: string, orderDesc: boolean }>({ head: "name", orderDesc: false });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const onOrderChange = (head: string, orderDesc: boolean) => {
    setOrderState({ head, orderDesc });
  };

  const resetPage = useCallback(() => {
    setPageState(0);
  }, []);

  const {
    filters,
    handleFilterSubmit,
    clearFilter: handleFilterClear,
    clearAllFilters,
    displayFilters
  } = useCustomersFilters(resetPage);

  const searchOrder = useMemo(
    () => toSearchOrder(orderState.head, orderState.orderDesc),
    [orderState]
  );

  const loadCustomersWithFilters = useCallback(async () => {
    if (!filters) return;

    setIsLoading(true);
    try {
      const queries: SearchQuery[] = getQueriesFromFilter(filters);
      const searchOptions: SearchOptions = {
        paginate: {
          pageSize,
          pageNumber: pageState,
        },
        order: searchOrder,
        queries,
      };

      const response = await searchWithFilters<EasyTech.Customer>(
        "customers",
        searchOptions,
        false,
        true
      );

      const customers = response?.data || [];
      if (customers.length > 0) {
        const ids = customers.map((c) => c.id).join(",");
        const recorded = await api.get(`admin/customers/recorded-data?ids=${ids}`) as Record<string, EasyTech.CustomerRecordedData>;
        setCustomersPageState(customers.map((customer) => ({
          ...customer,
          recordedData: recorded[customer.id] || emptyRecordedData()
        })));
        setPageNumberState(response.totalPages);
      } else {
        setCustomersPageState([]);
        setPageNumberState(1);
      }
    } catch (error) {
      console.error(error);
      setCustomersPageState([]);
      setPageNumberState(1);
    } finally {
      setIsLoading(false);
    }
  }, [searchOrder, filters, pageState, pageSize]);

  useEffect(() => {
    loadCustomersWithFilters();
  }, [loadCustomersWithFilters]);

  const openMergeDrawer = () => {
    dispatch({
      type: "open",
      title: t("admin:customers.mergeTitle"),
      content: <MergeCustomersDrawer onMerged={loadCustomersWithFilters} />
    });
  };

  const headerActions = (
    <>
      <Button size="sm" color="blue" onClick={openMergeDrawer}>
        {t("admin:customers.merge")}
      </Button>
      <FilterDrawer<EasyTech.CustomerFilterType>
        FormComponent={CustomerFilterForm}
        onSubmit={handleFilterSubmit}
        entityName="Customers"
        data={filters}
      />
    </>
  );

  const filterBadgesComponent = (
    <FiltersDisplay
      filters={displayFilters}
      onClearFilter={handleFilterClear}
      onClearAll={clearAllFilters}
      size="sm"
      compact={true}
    />
  );

  const mainContent =
    customersPageState && customersPageState.length > 0 ? (
      <AdminCustomersTable
        customers={customersPageState}
        onHeadClick={onOrderChange}
        selectedHead={orderState.head}
        orderDesc={orderState.orderDesc}
        onChange={loadCustomersWithFilters}
      />
    ) : (
      <div className="text-center py-8 bg-blue-gray-50 rounded-lg">
        <p className="text-lg text-blue-gray-700">{t("client:noData")}</p>
        <p className="text-sm text-blue-gray-500 mt-2">
          {filters && Object.keys(filters).length > 0
            ? t("client:noData:filters")
            : t("client:noData:add")}
        </p>
      </div>
    );

  const paginationComponent =
    customersPageState && customersPageState.length > 0 ? (
      <Pagination
        actualPage={pageState}
        pageNumber={pageNumberState}
        changePage={setPageState}
      />
    ) : null;

  return (
    <GenericListLayout
      entityName={t("admin:customers.title")}
      isLoading={isLoading}
      headerActions={headerActions}
      filterBadges={filterBadgesComponent}
      content={mainContent}
      pagination={paginationComponent}
      className="admin-customers"
      showBreadcrumb={false}
    />
  );
}
